import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, User, ShoppingCart, Bell, Menu, X } from 'lucide-react';
import { useNotifications } from './NotificationContext';
import logo from '../assets/logonobg.png';
import LoginPromptModal from './LoginPromptModal';

const NavBar = ({ isLoggedIn, setIsLoggedIn, onSearch, products = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, unreadCount, markAsRead, markAllRead } = useNotifications();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifFilter, setNotifFilter] = useState('all'); // 'all' or 'unread'
  const [notifLimit, setNotifLimit] = useState(3);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const searchRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter suggestions based on search query
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.category && product.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (product.tags && product.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
      ).slice(0, 5); // Limit to 5 suggestions
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, products]);

  const handleNotifToggle = () => {
    if (!showNotifications) {
      setNotifLimit(3);
      setNotifFilter('all');
    }
    setShowNotifications(!showNotifications);
  };

  const handleUserIconClick = () => {
    if (isLoggedIn) {
      setShowDropdown(!showDropdown);
    } else {
      navigate('/login');
    }
  };

  const handleProfileClick = () => {
    setShowDropdown(false);
    navigate('/profile');
  };

  const handleLogoutClick = () => {
    setShowDropdown(false);
    if (setIsLoggedIn) {
      setIsLoggedIn(false);
    } else {
      localStorage.setItem('logoutSuccess', 'true');
      navigate('/login');
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      if (onSearch) {
        onSearch(searchQuery);
      }
      setSearchQuery(''); // Clear search input
      setShowSearchSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (onSearch) {
      onSearch(suggestion.name);
    }
    setSearchQuery(''); // Clear search input
    setShowSearchSuggestions(false);
  };

  const handleSearchFocus = () => {
    setShowSearchSuggestions(true);
  };

  const handleLogoClick = () => {
    const newCount = logoClickCount + 1;
    if (newCount === 3) {
      navigate('/admin-login');
      setLogoClickCount(0);
    } else {
      setLogoClickCount(newCount);
      // Reset count after 2 seconds of inactivity
      setTimeout(() => setLogoClickCount(0), 2000);
    }
  };

  const styles = {
    topHeader: {
      backgroundColor: '#14291F',
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%'
    },
    logo: {
      height: '100%',
      maxHeight: windowWidth < 768 ? '40px' : '55px',
      objectFit: 'contain'
    },
    brandSection: {
      backgroundColor: '#ffffff',
      padding: windowWidth < 768 ? '15px 20px' : '20px 60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: '1px solid #e5e7eb',
      position: 'relative',
      minHeight: '70px'
    },
    searchWrapper: {
      position: 'relative',
      width: windowWidth < 768 ? '100%' : '300px',
      display: windowWidth < 768 ? (isMobileMenuOpen ? 'block' : 'none') : 'block',
      order: windowWidth < 768 ? 3 : 1
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #e5e7eb',
      padding: '6px 10px',
      width: '100%',
      borderRadius: '4px',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s'
    },
    searchContainerFocused: {
      borderColor: '#2E4B4C',
      boxShadow: '0 0 0 3px rgba(46, 75, 76, 0.1)'
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      fontSize: '14px',
      marginLeft: '8px',
      width: '100%',
      backgroundColor: 'transparent'
    },
    searchButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    suggestionsDropdown: {
      position: 'absolute',
      top: '100%',
      left: '0',
      right: '0',
      marginTop: '4px',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 1000,
      maxHeight: '400px',
      overflowY: 'auto'
    },
    suggestionSection: {
      padding: '12px 0'
    },
    suggestionTitle: {
      fontSize: '11px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '0 16px',
      marginBottom: '8px'
    },
    suggestionItem: {
      padding: '10px 16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'background-color 0.2s'
    },
    suggestionIcon: {
      width: '36px',
      height: '36px',
      backgroundColor: '#f3f4f6',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    suggestionText: {
      flex: 1
    },
    suggestionName: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1f2937',
      marginBottom: '2px'
    },
    suggestionCategory: {
      fontSize: '12px',
      color: '#6b7280'
    },
    popularSearches: {
      padding: '12px 16px',
      borderTop: '1px solid #f3f4f6'
    },
    popularTag: {
      display: 'inline-block',
      padding: '6px 12px',
      backgroundColor: '#f3f4f6',
      borderRadius: '16px',
      fontSize: '13px',
      color: '#4b5563',
      cursor: 'pointer',
      marginRight: '8px',
      marginBottom: '8px',
      transition: 'all 0.2s'
    },
    emptyState: {
      padding: '24px 16px',
      textAlign: 'center',
      color: '#6b7280',
      fontSize: '14px'
    },
    brandTitle: {
      fontFamily: 'Abril Fatface, serif',
      fontSize: windowWidth < 768 ? '20px' : '32px',
      letterSpacing: windowWidth < 768 ? '1px' : '2px',
      cursor: 'pointer',
      position: windowWidth < 768 ? 'static' : 'absolute',
      left: '50%',
      transform: windowWidth < 768 ? 'none' : 'translateX(-50%)',
      order: windowWidth < 768 ? 2 : 2
    },
    iconContainer: {
      display: 'flex',
      gap: windowWidth < 768 ? '10px' : '16px',
      alignItems: 'center',
      order: windowWidth < 768 ? 4 : 3
    },
    userIconWrapper: {
      position: 'relative'
    },
    iconBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#2E4B4C',
      padding: '5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    },
    badge: {
      position: 'absolute',
      top: '-4px',
      right: '-4px',
      backgroundColor: '#ef4444',
      color: '#ffffff',
      fontSize: '10px',
      fontWeight: '700',
      minWidth: '16px',
      height: '16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 4px',
      border: '2px solid #ffffff'
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      right: '0',
      marginTop: '8px',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      minWidth: '160px',
      zIndex: 1000,
      overflow: 'hidden'
    },
    dropdownItem: {
      padding: '12px 16px',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#2E4B4C',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'background-color 0.2s',
      fontWeight: '500'
    },
    notificationDropdown: {
      position: 'absolute',
      top: '100%',
      right: '0',
      marginTop: '8px',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
      width: windowWidth < 768 ? 'calc(100vw - 40px)' : '360px',
      right: windowWidth < 768 ? '-10px' : '0',
      zIndex: 1000,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    },
    notifHeader: {
      padding: '16px',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#ffffff'
    },
    notifTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1f2937'
    },
    notifMarkAll: {
      fontSize: '13px',
      color: '#AE8B59',
      cursor: 'pointer',
      fontWeight: '600',
      border: 'none',
      background: 'none',
      padding: '0'
    },
    notifList: {
      maxHeight: '400px',
      overflowY: 'auto',
      backgroundColor: '#ffffff'
    },
    notifItem: (isRead) => ({
      padding: '12px 16px',
      borderBottom: '1px solid #f3f4f6',
      cursor: 'pointer',
      display: 'flex',
      gap: '12px',
      backgroundColor: isRead ? '#ffffff' : '#f0f9f1',
      transition: 'background-color 0.2s',
      position: 'relative'
    }),
    notifIcon: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: '#2E4B4C',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      flexShrink: 0
    },
    notifContent: {
      flex: 1
    },
    notifMessage: {
      fontSize: '14px',
      color: '#374151',
      marginBottom: '4px',
      lineHeight: '1.4'
    },
    notifTime: {
      fontSize: '12px',
      color: '#9ca3af'
    },
    notifUnreadDot: {
      width: '10px',
      height: '10px',
      backgroundColor: '#2ecc71',
      borderRadius: '50%',
      position: 'absolute',
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)'
    },
    notifFooter: {
      padding: '12px',
      borderTop: '1px solid #f3f4f6',
      textAlign: 'center',
      backgroundColor: '#f9fafb'
    },
    notifSeeAll: {
      fontSize: '14px',
      color: '#2E4B4C',
      fontWeight: '600',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    navigation: {
      backgroundColor: '#ffffff',
      display: windowWidth < 768 ? (isMobileMenuOpen ? 'flex' : 'none') : 'flex',
      flexDirection: windowWidth < 768 ? 'column' : 'row',
      justifyContent: 'center',
      gap: windowWidth < 768 ? '0' : '70px',
      padding: windowWidth < 768 ? '0' : '14px 0',
      borderBottom: '1px solid #e5e7eb',
      position: windowWidth < 768 ? 'absolute' : 'static',
      top: '120px', // ~60px top bar + ~60px brand section
      left: 0,
      right: 0,
      zIndex: 999,
      boxShadow: windowWidth < 768 ? '0 10px 15px rgba(0,0,0,0.05)' : 'none'
    },
    mobileMenuBtn: {
      display: windowWidth < 768 ? 'flex' : 'none',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#2E4B4C',
      order: 1
    },
    navLink: (isActive) => ({
      cursor: 'pointer',
      fontSize: windowWidth < 768 ? '16px' : '15px',
      fontWeight: isActive ? '600' : '400',
      color: isActive ? '#2E4B4C' : '#6b7280',
      borderBottom: windowWidth < 768 ? 'none' : (isActive ? '2px solid #2E4B4C' : '2px solid transparent'),
      borderLeft: windowWidth < 768 && isActive ? '4px solid #2E4B4C' : 'none',
      padding: windowWidth < 768 ? '16px 20px' : '0 0 6px 0',
      minHeight: windowWidth < 768 ? '52px' : 'auto',
      display: windowWidth < 768 ? 'flex' : 'inline',
      alignItems: 'center',
      backgroundColor: windowWidth < 768 && isActive ? '#f9fafb' : 'transparent',
      transition: 'all 0.2s'
    }),
    loginButton: {
      backgroundColor: '#2E4B4C',
      color: '#ffffff',
      border: 'none',
      padding: '8px 20px',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    }
  };

  const popularSearches = [
    'Chair',
    'Table',
    'Sofa',
    'Bed',
    'Cabinet'
  ];

  return (
    <div>
      <LoginPromptModal isOpen={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} />
      {/* TOP GREEN BAR */}
      <div style={styles.topHeader}>
        <div style={styles.logoContainer} onClick={handleLogoClick}>
          <img src={logo} alt="Logo" width="150" height="40" style={{ ...styles.logo, cursor: 'pointer' }} />
        </div>
      </div>

      {/* BRAND SECTION */}
      <div style={styles.brandSection}>
        {/* MOBILE MENU TOGGLE */}
        <button 
          style={styles.mobileMenuBtn} 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* SEARCH */}
        <div style={styles.searchWrapper} ref={searchRef}>
          <form onSubmit={handleSearchSubmit}>
            <div
              style={{
                ...styles.searchContainer,
                ...(showSearchSuggestions ? styles.searchContainerFocused : {})
              }}
            >
              <button type="submit" style={styles.searchButton} aria-label="Submit search">
                <Search size={18} color="#9ca3af" />
              </button>
              <input
                type="text"
                placeholder="What are you looking for?"
                style={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleSearchFocus}
              />
            </div>
          </form>

          {/* SEARCH SUGGESTIONS DROPDOWN */}
          {showSearchSuggestions && (
            <div style={styles.suggestionsDropdown}>
              {searchQuery.trim() && suggestions.length > 0 ? (
                <div style={styles.suggestionSection}>
                  <div style={styles.suggestionTitle}>Suggestions</div>
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      style={styles.suggestionItem}
                      onClick={() => handleSuggestionClick(suggestion)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={styles.suggestionIcon}>
                        <Search size={16} color="#9ca3af" />
                      </div>
                      <div style={styles.suggestionText}>
                        <div style={styles.suggestionName}>{suggestion.name}</div>
                        {suggestion.category && (
                          <div style={styles.suggestionCategory}>{suggestion.category}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchQuery.trim() && suggestions.length === 0 ? (
                <div style={styles.emptyState}>
                  No results found for "{searchQuery}"
                </div>
              ) : (
                <>
                  <div style={styles.suggestionSection}>
                    <div style={styles.suggestionTitle}>Popular Searches</div>
                    <div style={styles.popularSearches}>
                      {popularSearches.map((term, index) => (
                        <div
                          key={index}
                          style={styles.popularTag}
                          onClick={() => {
                            if (onSearch) {
                              onSearch(term);
                            }
                            setSearchQuery(''); // Clear search input
                            setShowSearchSuggestions(false);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#2E4B4C';
                            e.currentTarget.style.color = '#ffffff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                            e.currentTarget.style.color = '#4b5563';
                          }}
                        >
                          {term}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* TITLE */}
        <div style={styles.brandTitle} onClick={() => navigate('/')}>
          UKIT KAPAMPANGAN
        </div>

        {/* ICONS */}
        <div style={styles.iconContainer}>
          {isLoggedIn ? (
            <>
              {/* User Icon with Dropdown */}
              <div style={styles.userIconWrapper} ref={dropdownRef}>
                <button style={styles.iconBtn} onClick={handleUserIconClick} aria-label="User profile menu">
                  <User size={22} />
                </button>

                {/* Dropdown Menu */}
                {showDropdown && (
                  <div style={styles.dropdown}>
                    <div
                      style={styles.dropdownItem}
                      onClick={handleProfileClick}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <User size={16} />
                      Profile
                    </div>
                    <div
                      style={{ ...styles.dropdownItem, borderBottom: 'none' }}
                      onClick={handleLogoutClick}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Logout
                    </div>
                  </div>
                )}
              </div>
              
              {/* Notification Bell with Dropdown */}
              <div style={{ position: 'relative' }} ref={notificationsRef}>
                <button 
                  style={styles.iconBtn} 
                  onClick={handleNotifToggle}
                  aria-label="Notifications"
                >
                  <Bell size={22} />
                  {unreadCount > 0 && <span style={styles.badge}>{unreadCount}</span>}
                </button>

                {showNotifications && (
                  <div style={styles.notificationDropdown}>
                    <div style={styles.notifHeader}>
                      <span style={styles.notifTitle}>Notifications</span>
                      {notifFilter === 'unread' && (
                        <button 
                          style={styles.notifMarkAll} 
                          onClick={() => { markAllRead(); setShowNotifications(false); }}
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '16px', padding: '0 16px', borderBottom: '1px solid #f3f4f6' }}>
                      <button 
                        style={{ background: 'none', border: 'none', padding: '12px 0', cursor: 'pointer', fontWeight: '600', color: notifFilter === 'all' ? '#2E4B4C' : '#9ca3af', borderBottom: notifFilter === 'all' ? '2px solid #2E4B4C' : '2px solid transparent' }}
                        onClick={() => { setNotifFilter('all'); setNotifLimit(3); }}
                      >
                        All
                      </button>
                      <button 
                        style={{ background: 'none', border: 'none', padding: '12px 0', cursor: 'pointer', fontWeight: '600', color: notifFilter === 'unread' ? '#2E4B4C' : '#9ca3af', borderBottom: notifFilter === 'unread' ? '2px solid #2E4B4C' : '2px solid transparent' }}
                        onClick={() => { setNotifFilter('unread'); setNotifLimit(3); }}
                      >
                        Unread
                      </button>
                    </div>

                    <div style={styles.notifList}>
                      {(() => {
                        const filtered = notifications.filter(n => notifFilter === 'all' || !n.isRead);
                        if (filtered.length === 0) {
                          return (
                            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                              No {notifFilter === 'unread' ? 'unread ' : ''}notifications yet.
                            </div>
                          );
                        }
                        return filtered.slice(0, notifLimit).map((notif) => (
                          <div 
                            key={notif._id} 
                            style={styles.notifItem(notif.isRead)}
                            onClick={() => {
                              markAsRead(notif._id);
                              setShowNotifications(false);
                              if (notif.link) navigate(notif.link);
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = notif.isRead ? '#f9fafb' : '#e6f4e8'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = notif.isRead ? '#ffffff' : '#f0f9f1'}
                          >
                            <div style={styles.notifIcon}>
                              <Bell size={18} />
                            </div>
                            <div style={styles.notifContent}>
                              <div style={styles.notifMessage}>{notif.message}</div>
                              <div style={styles.notifTime}>
                                {new Date(notif.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                            {!notif.isRead && <div style={styles.notifUnreadDot} />}
                          </div>
                        ));
                      })()}
                    </div>

                    <div style={styles.notifFooter}>
                      {(() => {
                        const filtered = notifications.filter(n => notifFilter === 'all' || !n.isRead);
                        if (filtered.length > notifLimit && notifLimit < 50) {
                          return (
                            <div 
                              style={styles.notifSeeAll} 
                              onClick={() => setNotifLimit(50)}
                            >
                              See More
                            </div>
                          );
                        }
                        return (
                          <div 
                            style={styles.notifSeeAll} 
                            onClick={() => { navigate('/notifications'); setShowNotifications(false); }}
                          >
                            Go to Notifications Page
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Cart Icon */}
              <button style={styles.iconBtn} onClick={() => navigate('/cart')} aria-label="Shopping cart">
                <ShoppingCart size={22} />
              </button>
            </>
          ) : (
            /* Login Button when not logged in */
            <button
              style={styles.loginButton}
              onClick={() => navigate('/login')}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f3a3b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2E4B4C';
              }}
            >
              Login
            </button>
          )}
        </div>
      </div>

      {/* NAV LINKS */}
      <div style={styles.navigation}>
        <span style={styles.navLink(location.pathname === '/' || location.pathname === '/home')} onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }}>
          Home
        </span>
        <span style={styles.navLink(location.pathname === '/shop' || location.pathname.startsWith('/product'))} onClick={() => { navigate('/shop'); setIsMobileMenuOpen(false); }}>
          Shop
        </span>
        <span style={styles.navLink(location.pathname === '/design')} onClick={() => { navigate('/design'); setIsMobileMenuOpen(false); }}>
          Virtual Showroom
        </span>
        <span style={styles.navLink(location.pathname === '/customize')} onClick={() => { 
          if (isLoggedIn) {
            navigate('/customize'); 
          } else {
            setShowLoginPrompt(true);
          }
          setIsMobileMenuOpen(false); 
        }}>
          Custom Furniture Request
        </span>
        <span style={styles.navLink(location.pathname === '/gallery')} onClick={() => { navigate('/gallery'); setIsMobileMenuOpen(false); }}>
          Gallery
        </span>
        <span style={styles.navLink(location.pathname === '/contact')} onClick={() => { navigate('/contact'); setIsMobileMenuOpen(false); }}>
          Contact Us
        </span>
      </div>
    </div>
  );
};

export default NavBar;